'use client';

import { Popover as BasePopover } from '@base-ui/react/popover';
import type { ReactElement, ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './popover.module.scss';

export type PopoverProps = {
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  label: string;
  /** The portal leaves the page tree, so a themed page passes its theme. */
  theme?: 'editorial' | undefined;
  className?: string | undefined;
};

/** Non-modal popover with outside-click and Escape dismissal. */
export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  side = 'bottom',
  align = 'end',
  sideOffset = 10,
  label,
  theme,
  className,
}: PopoverProps) {
  return (
    <BasePopover.Root
      {...(open === undefined ? {} : { open })}
      onOpenChange={(next) => onOpenChange?.(next)}
    >
      <BasePopover.Trigger render={trigger} />
      <BasePopover.Portal>
        <BasePopover.Positioner
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={cx(styles.positioner)}
          {...(theme ? { 'data-theme': theme } : {})}
        >
          <BasePopover.Popup className={cx(styles.popup, className)} aria-label={label}>
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}

export function PopoverClose({
  className,
  children,
  label,
}: {
  className?: string | undefined;
  children: ReactNode;
  label: string;
}) {
  return (
    <BasePopover.Close className={cx(className)} aria-label={label}>
      {children}
    </BasePopover.Close>
  );
}
